/**
 * Time KPI Helper
 * Utility functions for formatting and processing user time KPI data
 */

/**
 * Format milliseconds to human-readable time
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string
 */
export const formatTimeFromMilliseconds = (milliseconds) => {
  if (!milliseconds || milliseconds <= 0) {
    return '0m';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

/**
 * Format time for chart display
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time for charts
 */
export const formatTimeForChart = (milliseconds) => {
  if (!milliseconds || milliseconds <= 0) {
    return '0';
  }

  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Get activity level based on event counts (single source of truth).
 * This is the UNIFIED activity level logic used in both the user table and the dialog.
 *
 * Uses the sum of: login events, submitted assessments, level progress,
 * devtips completed, and travelbuddy meetings completed.
 *
 * @param {Object} activityCounts - Object with activity count fields
 * @param {Object} translations - Translation object with activity level labels
 * @returns {Object} Activity level with color, label, and level
 */
export const getActivityLevelFromCounts = (activityCounts, translations = null) => {
  const loginEventsCount = activityCounts?.loginEventsCount || 0;
  const submittedAssessmentsCount = activityCounts?.submittedAssessmentsCount || 0;
  const levelProgressCount = activityCounts?.levelProgressCount || 0;
  const devTipsCompletedCount = activityCounts?.devTipsCompletedCount || 0;
  const travelBuddyCompletedCount = activityCounts?.travelBuddyCompletedCount || 0;

  const totalActivities = loginEventsCount
    + submittedAssessmentsCount
    + levelProgressCount
    + devTipsCompletedCount
    + travelBuddyCompletedCount;

  if (totalActivities >= 10) {
    return {
      level: 'high',
      color: '#22c55e',
      label: translations?.veryActive || 'Very Active',
    };
  }
  if (totalActivities >= 5) {
    return {
      level: 'medium',
      color: '#eab308',
      label: translations?.active || 'Active',
    };
  }
  if (totalActivities >= 2) {
    return {
      level: 'low',
      color: '#f97316',
      label: translations?.moderate || 'Moderate',
    };
  }
  if (totalActivities >= 1) {
    return {
      level: 'low',
      color: '#f97316',
      label: translations?.lowActivity || 'Low Activity',
    };
  }
  return {
    level: 'inactive',
    color: '#ef4444',
    label: translations?.inactive || 'Inactive',
  };
};

/**
 * Get activity level based on time spent (legacy / fallback).
 * Kept for backward compatibility when activity counts are not available.
 * @param {number} milliseconds - Time in milliseconds
 * @param {Object} translations - Translation object with activity level labels
 * @returns {Object} Activity level with color and label
 */
export const getActivityLevel = (milliseconds, translations = null) => {
  const hours = milliseconds / (1000 * 60 * 60);

  if (hours >= 5) {
    return {
      level: 'high',
      color: '#22c55e',
      label: translations?.veryActive || 'Very Active',
    };
  } if (hours >= 2) {
    return {
      level: 'medium',
      color: '#eab308',
      label: translations?.active || 'Active',
    };
  } if (hours >= 0.5) {
    return {
      level: 'low',
      color: '#f97316',
      label: translations?.moderate || 'Moderate',
    };
  }
  return {
    level: 'inactive',
    color: '#ef4444',
    label: translations?.inactive || 'Inactive',
  };
};

/**
 * Extract chart data from KPI response
 * @param {Object} kpiData - KPI data from API
 * @returns {Object} Formatted chart data
 */
export const extractChartData = (kpiData) => {
  // Handle different data structures
  let metrics = null;

  // NEW: Check sessionMetrics first (from batch activity API)
  if (kpiData?.sessionMetrics?.totalTimeSpent > 0) {
    metrics = {
      totalTimeSpent: kpiData.sessionMetrics.totalTimeSpent,
      totalSessions: kpiData.sessionMetrics.totalSessions || 0,
      activeDays: kpiData.sessionMetrics.activeDays ?? 0,
    };
  } else if (kpiData?.stored?.metrics) {
    metrics = kpiData.stored.metrics;
  } else if (kpiData?.stored?.timeMetrics) {
    metrics = kpiData.stored.timeMetrics;
  } else if (kpiData?.calculation?.timeMetrics) {
    metrics = kpiData.calculation.timeMetrics;
  } else if (kpiData?.calculation?.metrics) {
    metrics = kpiData.calculation.metrics;
  } else if (kpiData?.metrics) {
    metrics = kpiData.metrics;
  } else if (kpiData?.timeMetrics) {
    metrics = kpiData.timeMetrics;
  }

  if (!metrics) {
    // Return empty chart with a message
    return {
      labels: ['No Data'],
      datasets: [{
        label: 'No Activity Data',
        data: [0],
        backgroundColor: 'rgba(156, 163, 175, 0.2)',
        borderColor: 'rgba(156, 163, 175, 1)',
        borderWidth: 2,
      }],
    };
  }

  // Extract daily activity data if available
  if (metrics.dailyActivity && Array.isArray(metrics.dailyActivity)) {
    const labels = metrics.dailyActivity.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const data = metrics.dailyActivity.map(
      (item) => Math.round((item.totalTime / (1000 * 60 * 60)) * 100) / 100,
    );

    return {
      labels,
      datasets: [
        {
          label: 'Hours per Day',
          data,
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2,
          tension: 0.4,
        },
      ],
    };
  }

  // Fallback: create a bar chart with multiple metrics
  const totalTime = metrics.totalTimeSpent || metrics.totalTime || 0;
  // const totalSessions = metrics.totalSessions || 0;
  const averageSessionDuration = metrics.averageSessionDuration || 0;
  const hours = Math.round((totalTime / (1000 * 60 * 60)) * 100) / 100;
  const avgHours = Math.round((averageSessionDuration / (1000 * 60 * 60)) * 100) / 100;

  return {
    // labels: ['Total Hours', 'Sessions', 'Avg Session (hrs)'],
    labels: ['Total Hours', 'Avg Session (hrs)'],
    datasets: [
      {
        label: 'Activity Metrics',
        // data: [hours, totalSessions, avgHours],
        data: [hours, avgHours],
        backgroundColor: [
          'rgba(59, 130, 246, 0.6)',
          // 'rgba(34, 197, 94, 0.6)',
          'rgba(251, 191, 36, 0.6)',
        ],
        // borderColor: ['rgba(59, 130, 246, 1)', 'rgba(34, 197, 94, 1)', 'rgba(251, 191, 36, 1)'],
        borderColor: ['rgba(59, 130, 246, 1)', 'rgba(251, 191, 36, 1)'],
        borderWidth: 2,
      },
    ],
  };
};

/**
 * Get activity summary for any period
 * @param {Object} kpiData - KPI data from API
 * @param {Object} translations - Translation object with activity level labels
 * @param {Object} activityCounts - Optional activity counts for unified activity level
 * @returns {Object} Activity summary
 */
export const getActivitySummary = (kpiData, translations = null, activityCounts = null) => {
  // Handle different data structures (same as extractChartData)
  let metrics = null;

  // NEW: Check sessionMetrics first (from batch activity API)
  if (kpiData?.sessionMetrics?.totalTimeSpent > 0) {
    metrics = {
      totalTimeSpent: kpiData.sessionMetrics.totalTimeSpent,
      totalSessions: kpiData.sessionMetrics.totalSessions || 0,
      activeDays: kpiData.sessionMetrics.activeDays ?? 0,
    };
  } else if (kpiData?.stored?.metrics) {
    metrics = kpiData.stored.metrics;
  } else if (kpiData?.stored?.timeMetrics) {
    metrics = kpiData.stored.timeMetrics;
  } else if (kpiData?.calculation?.timeMetrics) {
    metrics = kpiData.calculation.timeMetrics;
  } else if (kpiData?.calculation?.metrics) {
    metrics = kpiData.calculation.metrics;
  } else if (kpiData?.metrics) {
    metrics = kpiData.metrics;
  } else if (kpiData?.timeMetrics) {
    metrics = kpiData.timeMetrics;
  }

  // Determine activity level: prefer event-count-based (unified with table),
  // fall back to time-based only when counts are unavailable.
  const resolveActivityLevel = (totalTimeMs) => {
    if (activityCounts) {
      return getActivityLevelFromCounts(activityCounts, translations);
    }
    return getActivityLevel(totalTimeMs, translations);
  };

  if (!metrics) {
    return {
      totalTime: 0,
      averageTime: 0,
      activeDays: 0,
      formattedTotal: '0m',
      formattedAverage: '0m',
      activityLevel: resolveActivityLevel(0),
    };
  }

  const totalTime = metrics.totalTimeSpent || metrics.totalTime || 0;
  const activeDays = metrics.activeDays ?? 0;
  const averageTime = activeDays > 0 ? totalTime / activeDays : 0;

  return {
    totalTime,
    averageTime,
    activeDays,
    formattedTotal: formatTimeFromMilliseconds(totalTime),
    formattedAverage: formatTimeFromMilliseconds(averageTime),
    activityLevel: resolveActivityLevel(totalTime),
  };
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '-';

  try {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '-';
  }
};

/**
 * Get time period label
 * @param {string} periodType - Period type (daily, weekly, monthly)
 * @returns {string} Human-readable period label
 */
export const getPeriodLabel = (periodType) => {
  const labels = {
    daily: 'Last 24 Hours',
    weekly: 'Last Week',
    monthly: 'Last Month',
  };

  return labels[periodType] || periodType;
};
